"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const CONTENT = {
  fr: {
    breadcrumbHome: "Accueil",
    breadcrumbCurrent: "Débarras de maison",
    h1: "Débarras de maison à Bruxelles",
    highlight: "Maison unifamiliale, maison de maître ou maison mitoyenne, sur un ou plusieurs niveaux.",
    heroText:
      "Vider une maison entière — du grenier à la cave, garage et jardin compris — demande une organisation différente d'un simple débarras de studio : plusieurs niveaux à traiter, souvent une échéance fixée par un notaire, un syndic ou un compromis de vente, et des objets accumulés parfois depuis plusieurs générations. Nous intervenons dans les 19 communes de la Région bruxelloise pour prendre en charge ce débarras dans son ensemble : estimation sur place, tri entre rachat, don et évacuation, puis remise en état sommaire des pièces vidées. Le devis est gratuit et établi sur mesure, car il n'existe pas de prix unique tant que le volume réel n'a pas été vu ou décrit.",
    freeQuote: "Demander un devis gratuit",
    process: {
      title: "Comment se déroule le débarras d'une maison, du premier contact à la remise des lieux",
      p: "L'intervention suit toujours la même logique, adaptée ensuite au nombre de pièces et de niveaux réellement concernés.",
      steps: [
        { n: "1", title: "Estimation", text: "Description de la maison (niveaux, volume, accès) par téléphone ou sur place, pour établir un devis gratuit." },
        { n: "2", title: "Tri sur place", text: "Passage pièce par pièce : objets rachetables mis de côté, reste réparti entre don et évacuation." },
        { n: "3", title: "Évacuation", text: "Chargement et évacuation de l'ensemble des niveaux — grenier, étages, cave, garage, jardin." },
        { n: "4", title: "Remise en état", text: "Nettoyage sommaire des pièces vidées et remise des lieux, notamment avant une vente ou une location." },
      ],
    },
    situations: {
      title: "Dans quels cas faire appel à un débarras de maison à Bruxelles ?",
      pBefore: "La plupart de nos interventions sur des maisons entières s'inscrivent dans l'une de ces situations. Une ",
      pLink: "succession",
      pAfter: " impose souvent un calendrier serré fixé par le notaire ; une vente ou une mise en location impose plutôt un bien à libérer complètement et rapidement.",
      items: [
        "Succession d'une maison familiale",
        "Vente d'un bien à libérer complètement",
        "Départ en maison de repos",
        "Maison encombrée depuis plusieurs années",
        "Séparation ou divorce",
        "Remise en location après un long séjour",
      ],
    },
    prix: {
      title: "Le prix d'un débarras de maison dépend de plusieurs facteurs",
      p: "Une maison entière représente rarement un volume comparable d'un bien à l'autre : le prix se construit donc sur des critères concrets plutôt que sur un tarif forfaitaire.",
      items: [
        "Volume total à évacuer et nombre de niveaux",
        "Accès : escaliers, ascenseur absent, parking à proximité",
        "Présence d'encombrants lourds ou spécifiques (électroménager, gravats)",
        "Objets rachetables identifiés, déduits du montant du devis",
      ],
      pAfterBefore: "Nous préférons établir un devis après avoir vu ou fait décrire la maison précisément, plutôt que d'annoncer un chiffre approximatif qui ne correspondrait pas à la réalité du chantier. Pour une première idée des ordres de grandeur, notre page ",
      pAfterLink1: "grille de tarifs",
      pAfterMid: " détaille les critères pris en compte, et l'article ",
      pAfterLink2: "combien coûte un vide-maison à Bruxelles",
      pAfterEnd: " explique la construction du devis plus en détail.",
    },
    tri: {
      badge: "Tri, évacuation et rachat",
      title: "Que devient le contenu de la maison ?",
      p1before: "Les maisons habitées depuis longtemps, ou héritées d'une génération précédente, recèlent souvent des objets oubliés : mobilier ancien, vaisselle, bijoux, argenterie ou tableaux rangés depuis des années. Nous les examinons systématiquement avant évacuation pour identifier ce qui peut être ",
      p1link: "racheté et déduit du devis",
      p1end: ".",
      p2before: "Ce qui n'est ni rachetable ni destiné au don est trié par catégorie — bois, métal, électroménager, gravats, textiles — avant évacuation vers les centres de tri adaptés. Ce tri se fait pièce par pièce, autant que possible, plutôt que de tout mélanger dans une même benne. Pour en savoir plus sur le devenir des meubles et objets évacués, consultez notre article ",
      p2link: "que deviennent les meubles après un débarras",
      p2end: ".",
    },
    acces: {
      title: "Accès, logistique et préparation avant notre passage",
      p1: "Il n'est pas nécessaire de trier ou de vider les pièces avant notre venue : le tri fait partie de notre travail. Il est en revanche utile de mettre de côté les documents administratifs, papiers d'identité ou objets personnels que vous souhaitez absolument conserver, et de nous signaler à l'avance les pièces ou meubles à exclure de l'intervention.",
      p2before: "Côté accès, chaque rue bruxelloise pose ses propres contraintes : stationnement limité, rue étroite ou à sens unique, escaliers étroits dans les maisons de maître. Ces éléments sont à signaler dès la demande de devis afin d'organiser l'équipe et le véhicule en conséquence. Notre article ",
      p2link: "checklist avant un vide-maison",
      p2end: " détaille les points à préparer selon votre situation.",
    },
    diff: {
      title: "Maison, appartement ou espace annexe : quelle formule choisir ?",
      pBefore: "Cette page couvre le débarras d'une maison entière, niveau par niveau. Si vous videz plutôt un ",
      link1: "appartement, un studio ou un immeuble avec syndic",
      pMid1: ", l'organisation et les contraintes d'accès diffèrent légèrement. À l'inverse, si seule une ",
      link2: "cave, un grenier ou un garage",
      pMid2: " doit être vidé, sans toucher au reste de la maison, une intervention plus ciblée est possible. Nous intervenons dans les ",
      link3: "19 communes de la Région bruxelloise",
      pEnd: ", quelle que soit la formule retenue.",
    },
    pourquoi: {
      title: "Pourquoi faire appel à nous pour vider une maison à Bruxelles",
      items: [
        "Devis gratuit et clair, établi avant toute intervention",
        "Examen des objets avant évacuation, pour un rachat quand c'est possible",
        "Une seule équipe qui suit le chantier du début à la remise des lieux",
        "Organisation adaptée au calendrier fixé par le notaire, le syndic ou l'agence",
      ],
      pBefore: "Vous pouvez consulter des exemples concrets d'interventions sur notre page ",
      pLink1: "réalisations",
      pMid: " ou lire les retours de clients sur notre page ",
      pLink2: "avis",
      pEnd: ".",
    },
    faq: {
      title: "Questions fréquentes sur le débarras de maison",
      items: [
        {
          q: "Combien coûte un débarras de maison à Bruxelles ?",
          a: "Le prix dépend du volume à évacuer, du nombre de niveaux, de l'accès (parking, escaliers, ascenseur) et de la présence d'objets rachetables qui viennent en déduction du devis. Il n'existe pas de tarif fixe pour une maison entière : la situation d'une maison mitoyenne de deux façades diffère de celle d'une maison de maître sur trois niveaux. Nous établissons un devis gratuit après avoir vu ou fait décrire précisément la maison, sans frais cachés au moment de la facturation.",
        },
        {
          q: "Combien de temps prend le débarras d'une maison entière ?",
          a: "La durée varie surtout selon le volume et le nombre de niveaux à traiter : une maison mitoyenne classique se vide généralement plus vite qu'une maison de maître avec grenier, cave et dépendances. L'accès joue aussi un rôle — escaliers étroits, absence de parking à proximité ou rue à sens unique peuvent rallonger l'intervention. Nous estimons la durée nécessaire dès le premier contact, sur base du volume décrit ou constaté sur place, afin d'organiser l'équipe et le camion en conséquence.",
        },
        {
          q: "Faut-il être présent pendant toute la durée du débarras ?",
          a: "Votre présence est surtout utile au début, pour valider ensemble ce qui doit être évacué, conservé ou examiné pour un rachat éventuel, et en fin d'intervention pour la remise des lieux. Entre les deux, une présence continue n'est pas indispensable si les consignes sont claires. Dans le cadre d'une succession ou d'une vente organisée par un notaire ou une agence, nous pouvons aussi coordonner l'accès directement avec la personne en charge du dossier.",
        },
        {
          q: "Comment sont pris en charge les meubles et objets de valeur trouvés dans la maison ?",
          a: "Avant toute évacuation, nous examinons les objets présents : mobilier ancien, vaisselle, bijoux, montres, argenterie ou tableaux peuvent faire l'objet d'un rachat, déduit du montant du débarras. Cette estimation reste soumise à l'état et à la nature réelle des objets — nous ne rachetons pas tout de façon systématique. Les meubles en bon état mais sans valeur de rachat sont, lorsque c'est possible, orientés vers le don plutôt que directement évacués en déchet.",
        },
        {
          q: "Que deviennent les encombrants et déchets qui ne peuvent être ni rachetés ni donnés ?",
          a: "Ce qui n'est ni rachetable ni réutilisable est trié par catégorie — bois, métal, électroménager, gravats, textiles — avant d'être évacué vers les centres de tri adaptés. Ce tri se fait autant que possible directement sur place, pièce par pièce, plutôt que de tout mélanger dans une même benne. Cette étape d'évacuation fait partie intégrante du débarras et est comprise dans le devis initial.",
        },
        {
          q: "La maison est difficile d'accès (rue étroite, pas de parking, plusieurs étages) : est-ce un problème ?",
          a: "Non, mais cela influence l'organisation de l'intervention. Nous adaptons le nombre de personnes, le type de véhicule et parfois les créneaux horaires en fonction de l'accès réel : rue étroite, stationnement limité, escaliers ou étages sans ascenseur. Ces éléments sont à signaler dès la demande de devis afin que l'estimation de prix et de durée soit la plus juste possible dès le départ.",
        },
        {
          q: "Faut-il trier ou vider les pièces avant votre passage ?",
          a: "Non, ce n'est pas nécessaire : nous prenons en charge le tri sur place, pièce par pièce. Il est en revanche utile de mettre de côté les documents administratifs, papiers d'identité ou objets personnels que vous souhaitez absolument conserver, pour éviter toute confusion le jour du débarras. Si certaines pièces ou certains meubles doivent être exclus de l'intervention, il suffit de nous le signaler à l'avance.",
        },
      ],
    },
    ctaFinal: {
      title: "Une maison à vider à Bruxelles ?",
      p: "Décrivez-nous votre situation par téléphone ou via le formulaire ci-dessous : nous revenons vers vous avec un devis gratuit, adapté au volume réel de votre maison.",
    },
  },
  nl: {
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Huis ontruimen",
    h1: "Huis ontruimen in Brussel",
    highlight: "Eengezinswoning, herenhuis of rijwoning, op één of meerdere niveaus.",
    heroText:
      "Een volledig huis leegmaken — van zolder tot kelder, garage en tuin inbegrepen — vraagt een andere organisatie dan een eenvoudige studio-ontruiming: meerdere niveaus om aan te pakken, vaak een deadline bepaald door een notaris, een syndicus of een compromis van verkoop, en voorwerpen die soms al generaties lang opgestapeld liggen. Wij komen tussen in de 19 gemeenten van het Brussels Gewest om deze ontruiming volledig op ons te nemen: schatting ter plaatse, sortering tussen inkoop, schenking en afvoer, en vervolgens een summiere opknapbeurt van de leeggemaakte kamers. De offerte is gratis en op maat opgesteld, want er bestaat geen uniforme prijs zolang het werkelijke volume niet gezien of beschreven is.",
    freeQuote: "Vraag een gratis offerte aan",
    process: {
      title: "Hoe verloopt de ontruiming van een huis, van het eerste contact tot de oplevering",
      p: "De interventie volgt altijd dezelfde logica, die vervolgens wordt afgestemd op het werkelijke aantal kamers en niveaus.",
      steps: [
        { n: "1", title: "Schatting", text: "Beschrijving van het huis (niveaus, volume, toegang) telefonisch of ter plaatse, om een gratis offerte op te stellen." },
        { n: "2", title: "Sortering ter plaatse", text: "Kamer per kamer: inkoopbare voorwerpen worden opzijgezet, de rest wordt verdeeld tussen schenking en afvoer." },
        { n: "3", title: "Afvoer", text: "Laden en afvoeren van alle niveaus — zolder, verdiepingen, kelder, garage, tuin." },
        { n: "4", title: "Opknapbeurt", text: "Summiere schoonmaak van de leeggemaakte kamers en oplevering van het pand, met name vóór een verkoop of verhuur." },
      ],
    },
    situations: {
      title: "In welke gevallen doet u een beroep op een huisontruiming in Brussel?",
      pBefore: "De meeste van onze interventies bij volledige huizen vallen onder een van deze situaties. Een ",
      pLink: "erfenis",
      pAfter: " legt vaak een strak tijdschema op, bepaald door de notaris; een verkoop of verhuur vereist eerder dat het pand volledig en snel wordt vrijgemaakt.",
      items: [
        "Erfenis van een familiewoning",
        "Verkoop van een pand dat volledig moet worden vrijgemaakt",
        "Vertrek naar een rusthuis",
        "Huis dat al jaren vol staat",
        "Scheiding of echtscheiding",
        "Opnieuw verhuren na een lang verblijf",
      ],
    },
    prix: {
      title: "De prijs van een huisontruiming hangt af van verschillende factoren",
      p: "Een volledig huis vertegenwoordigt zelden een vergelijkbaar volume van het ene pand tot het andere: de prijs wordt dus opgebouwd op basis van concrete criteria in plaats van een forfaitair tarief.",
      items: [
        "Totaal volume dat moet worden afgevoerd en aantal niveaus",
        "Toegang: trappen, ontbrekende lift, parkeren in de buurt",
        "Aanwezigheid van zware of specifieke grofvuil (huishoudapparaten, puin)",
        "Geïdentificeerde inkoopbare voorwerpen, afgetrokken van het offertebedrag",
      ],
      pAfterBefore: "Wij geven er de voorkeur aan een offerte op te stellen nadat we het huis precies hebben gezien of laten beschrijven, in plaats van een bij benadering cijfer aan te kondigen dat niet zou overeenstemmen met de realiteit van de werf. Voor een eerste idee van de grootteorde beschrijft onze pagina ",
      pAfterLink1: "tarievenoverzicht",
      pAfterMid: " de in aanmerking genomen criteria, en legt het artikel ",
      pAfterLink2: "hoeveel kost een huis leegmaken in Brussel",
      pAfterEnd: " de opbouw van de offerte in meer detail uit.",
    },
    tri: {
      badge: "Sortering, afvoer en inkoop",
      title: "Wat gebeurt er met de inhoud van het huis?",
      p1before: "Huizen die al lang bewoond zijn, of geërfd van een vorige generatie, bevatten vaak vergeten voorwerpen: antiek meubilair, servies, juwelen, zilverwerk of schilderijen die al jaren opgeborgen liggen. Wij bekijken ze systematisch vóór de afvoer om te bepalen wat kan worden ",
      p1link: "ingekocht en afgetrokken van de offerte",
      p1end: ".",
      p2before: "Wat niet inkoopbaar is en niet bestemd voor schenking, wordt gesorteerd per categorie — hout, metaal, huishoudapparaten, puin, textiel — vóór afvoer naar de geschikte sorteercentra. Deze sortering gebeurt zoveel mogelijk kamer per kamer, in plaats van alles in dezelfde container te mengen. Voor meer informatie over wat er met de afgevoerde meubels en voorwerpen gebeurt, raadpleeg ons artikel ",
      p2link: "wat gebeurt er met meubels na een ontruiming",
      p2end: ".",
    },
    acces: {
      title: "Toegang, logistiek en voorbereiding vóór onze komst",
      p1: "Het is niet nodig om de kamers vóór onze komst te sorteren of leeg te maken: sorteren maakt deel uit van ons werk. Het is echter nuttig om administratieve documenten, identiteitspapieren of persoonlijke voorwerpen die u absoluut wilt bewaren opzij te leggen, en ons vooraf te melden welke kamers of meubels van de interventie moeten worden uitgesloten.",
      p2before: "Wat de toegang betreft, heeft elke Brusselse straat haar eigen beperkingen: beperkt parkeren, smalle straat of eenrichtingsverkeer, smalle trappen in herenhuizen. Deze elementen moeten al bij de offerteaanvraag worden gemeld, zodat het team en het voertuig hierop kunnen worden afgestemd. Ons artikel ",
      p2link: "checklist vóór een huis leegmaken",
      p2end: " beschrijft de te voorziene punten volgens uw situatie.",
    },
    diff: {
      title: "Huis, appartement of bijruimte: welke formule kiezen?",
      pBefore: "Deze pagina behandelt de ontruiming van een volledig huis, niveau per niveau. Als u eerder een ",
      link1: "appartement, een studio of een gebouw met syndicus",
      pMid1: " leegmaakt, verschillen de organisatie en de toegangsbeperkingen enigszins. Omgekeerd, als enkel een ",
      link2: "kelder, een zolder of een garage",
      pMid2: " moet worden leeggemaakt, zonder de rest van het huis aan te raken, is een meer gerichte interventie mogelijk. Wij komen tussen in de ",
      link3: "19 gemeenten van het Brussels Gewest",
      pEnd: ", ongeacht de gekozen formule.",
    },
    pourquoi: {
      title: "Waarom een beroep doen op ons om een huis leeg te maken in Brussel",
      items: [
        "Gratis en duidelijke offerte, opgesteld vóór elke interventie",
        "Onderzoek van de voorwerpen vóór de afvoer, voor een inkoop wanneer mogelijk",
        "Eén enkel team dat de werf volgt van begin tot oplevering",
        "Organisatie afgestemd op het tijdschema bepaald door de notaris, de syndicus of het agentschap",
      ],
      pBefore: "U kunt concrete voorbeelden van interventies bekijken op onze pagina ",
      pLink1: "realisaties",
      pMid: " of de reacties van klanten lezen op onze pagina ",
      pLink2: "beoordelingen",
      pEnd: ".",
    },
    faq: {
      title: "Veelgestelde vragen over huisontruiming",
      items: [
        {
          q: "Hoeveel kost een huisontruiming in Brussel?",
          a: "De prijs hangt af van het volume dat moet worden afgevoerd, het aantal niveaus, de toegang (parkeren, trappen, lift) en de aanwezigheid van inkoopbare voorwerpen die van de offerte worden afgetrokken. Er bestaat geen vast tarief voor een volledig huis: de situatie van een rijwoning met twee gevels verschilt van die van een herenhuis met drie niveaus. Wij stellen een gratis offerte op nadat we het huis precies hebben gezien of laten beschrijven, zonder verborgen kosten bij de facturatie.",
        },
        {
          q: "Hoe lang duurt de ontruiming van een volledig huis?",
          a: "De duur varieert vooral naargelang het volume en het aantal te behandelen niveaus: een klassieke rijwoning wordt doorgaans sneller leeggemaakt dan een herenhuis met zolder, kelder en bijgebouwen. De toegang speelt ook een rol — smalle trappen, geen parkeergelegenheid in de buurt of een straat met eenrichtingsverkeer kunnen de interventie verlengen. Wij schatten de benodigde duur al bij het eerste contact in, op basis van het beschreven of ter plaatse vastgestelde volume, om het team en de vrachtwagen hierop af te stemmen.",
        },
        {
          q: "Moet ik aanwezig zijn tijdens de volledige duur van de ontruiming?",
          a: "Uw aanwezigheid is vooral nuttig aan het begin, om samen te bevestigen wat moet worden afgevoerd, bewaard of onderzocht voor een eventuele inkoop, en aan het einde van de interventie voor de oplevering. Daartussenin is een voortdurende aanwezigheid niet noodzakelijk als de instructies duidelijk zijn. In het kader van een erfenis of een verkoop georganiseerd door een notaris of agentschap kunnen wij de toegang ook rechtstreeks coördineren met de persoon die het dossier beheert.",
        },
        {
          q: "Hoe worden meubels en waardevolle voorwerpen in het huis behandeld?",
          a: "Vóór elke afvoer bekijken wij de aanwezige voorwerpen: antiek meubilair, servies, juwelen, horloges, zilverwerk of schilderijen kunnen worden ingekocht, afgetrokken van het ontruimingsbedrag. Deze schatting blijft afhankelijk van de staat en de werkelijke aard van de voorwerpen — wij kopen niet systematisch alles in. Meubels in goede staat maar zonder inkoopwaarde worden, indien mogelijk, naar schenking geleid in plaats van rechtstreeks als afval te worden afgevoerd.",
        },
        {
          q: "Wat gebeurt er met grofvuil en afval dat niet kan worden ingekocht of geschonken?",
          a: "Wat niet inkoopbaar of herbruikbaar is, wordt gesorteerd per categorie — hout, metaal, huishoudapparaten, puin, textiel — vóór afvoer naar de geschikte sorteercentra. Deze sortering gebeurt zoveel mogelijk rechtstreeks ter plaatse, kamer per kamer, in plaats van alles in dezelfde container te mengen. Deze afvoerfase maakt integraal deel uit van de ontruiming en is inbegrepen in de oorspronkelijke offerte.",
        },
        {
          q: "Het huis is moeilijk bereikbaar (smalle straat, geen parkeergelegenheid, meerdere verdiepingen): is dat een probleem?",
          a: "Nee, maar het beïnvloedt de organisatie van de interventie. Wij passen het aantal medewerkers, het type voertuig en soms de tijdslots aan op basis van de werkelijke toegang: smalle straat, beperkt parkeren, trappen of verdiepingen zonder lift. Deze elementen moeten al bij de offerteaanvraag worden gemeld, zodat de prijs- en duurschatting van bij het begin zo nauwkeurig mogelijk is.",
        },
        {
          q: "Moet ik de kamers sorteren of leegmaken vóór onze komst?",
          a: "Nee, dat is niet nodig: wij nemen de sortering ter plaatse op ons, kamer per kamer. Het is echter nuttig om administratieve documenten, identiteitspapieren of persoonlijke voorwerpen die u absoluut wilt bewaren opzij te leggen, om verwarring op de dag van de ontruiming te vermijden. Als bepaalde kamers of meubels van de interventie moeten worden uitgesloten, volstaat het ons dit vooraf te melden.",
        },
      ],
    },
    ctaFinal: {
      title: "Een huis leeg te maken in Brussel?",
      p: "Beschrijf ons uw situatie telefonisch of via het onderstaande formulier: wij komen bij u terug met een gratis offerte, afgestemd op het werkelijke volume van uw huis.",
    },
  },
  en: {
    breadcrumbHome: "Home",
    breadcrumbCurrent: "House clearance",
    h1: "House Clearance in Brussels",
    highlight: "Single-family house, townhouse or terraced house, on one or several levels.",
    heroText:
      "Clearing an entire house — from attic to cellar, garage and garden included — requires a different approach than a simple studio clearance: several levels to handle, often a deadline set by a notary, a syndic or a sale agreement, and items sometimes accumulated over several generations. We work across the 19 communes of the Brussels Region to take care of this clearance as a whole: on-site appraisal, sorting between buyback, donation and removal, then a basic tidy-up of the cleared rooms. The quote is free and tailor-made, since there is no single price until the actual volume has been seen or described.",
    freeQuote: "Request a free quote",
    process: {
      title: "How house clearance unfolds, from first contact to handover",
      p: "The intervention always follows the same logic, then adapted to the actual number of rooms and levels involved.",
      steps: [
        { n: "1", title: "Appraisal", text: "Description of the house (levels, volume, access) by phone or on site, to draw up a free quote." },
        { n: "2", title: "On-site sorting", text: "Room by room: items suitable for buyback are set aside, the rest is split between donation and removal." },
        { n: "3", title: "Removal", text: "Loading and clearing all levels — attic, floors, cellar, garage, garden." },
        { n: "4", title: "Tidy-up", text: "Basic cleaning of the cleared rooms and handover of the property, particularly before a sale or rental." },
      ],
    },
    situations: {
      title: "When should you call on house clearance services in Brussels?",
      pBefore: "Most of our interventions on entire houses fall under one of these situations. An ",
      pLink: "estate settlement",
      pAfter: " often imposes a tight schedule set by the notary; a sale or rental instead requires the property to be fully and quickly vacated.",
      items: [
        "Estate settlement of a family home",
        "Sale of a property to be fully vacated",
        "Move to a care home",
        "House cluttered for several years",
        "Separation or divorce",
        "Re-letting after a long stay",
      ],
    },
    prix: {
      title: "The price of house clearance depends on several factors",
      p: "An entire house rarely represents a comparable volume from one property to another: the price is therefore built on concrete criteria rather than a flat rate.",
      items: [
        "Total volume to be removed and number of levels",
        "Access: staircases, absence of a lift, nearby parking",
        "Presence of heavy or specific bulky items (appliances, rubble)",
        "Buyback-eligible items identified, deducted from the quote amount",
      ],
      pAfterBefore: "We prefer to draw up a quote after having seen or had the house precisely described, rather than announcing an approximate figure that wouldn't match the reality of the job. For a first idea of the price range, our ",
      pAfterLink1: "pricing overview",
      pAfterMid: " page details the criteria taken into account, and the article ",
      pAfterLink2: "how much does house clearance cost in Brussels",
      pAfterEnd: " explains how the quote is built in more detail.",
    },
    tri: {
      badge: "Sorting, removal and buyback",
      title: "What happens to the contents of the house?",
      p1before: "Houses lived in for a long time, or inherited from a previous generation, often hold forgotten items: antique furniture, tableware, jewellery, silverware or paintings stored away for years. We systematically examine them before removal to identify what can be ",
      p1link: "bought back and deducted from the quote",
      p1end: ".",
      p2before: "What is neither suitable for buyback nor for donation is sorted by category — wood, metal, appliances, rubble, textiles — before removal to the appropriate sorting centres. This sorting is done room by room as much as possible, rather than mixing everything into the same skip. To learn more about what happens to removed furniture and items, see our article ",
      p2link: "what happens to furniture after a clearance",
      p2end: ".",
    },
    acces: {
      title: "Access, logistics and preparation before our visit",
      p1: "There's no need to sort or empty the rooms before our visit: sorting is part of our job. It is, however, useful to set aside administrative documents, ID papers or personal items you absolutely want to keep, and to tell us in advance which rooms or pieces of furniture should be excluded from the intervention.",
      p2before: "In terms of access, every Brussels street has its own constraints: limited parking, a narrow or one-way street, narrow staircases in townhouses. These details should be flagged when requesting your quote so the team and vehicle can be organised accordingly. Our article ",
      p2link: "checklist before a house clearance",
      p2end: " details the points to prepare according to your situation.",
    },
    diff: {
      title: "House, apartment or ancillary space: which service fits your needs?",
      pBefore: "This page covers the clearance of an entire house, level by level. If you're instead clearing an ",
      link1: "apartment, a studio or a building with a syndic",
      pMid1: ", the organisation and access constraints differ slightly. Conversely, if only a ",
      link2: "cellar, an attic or a garage",
      pMid2: " needs to be cleared, without touching the rest of the house, a more targeted intervention is possible. We work across the ",
      link3: "19 communes of the Brussels Region",
      pEnd: ", whichever service you choose.",
    },
    pourquoi: {
      title: "Why choose us to clear a house in Brussels",
      items: [
        "Free, clear quote, drawn up before any intervention",
        "Examination of items before removal, for a buyback when possible",
        "A single team following the job from start to handover",
        "Organisation adapted to the schedule set by the notary, the syndic or the agency",
      ],
      pBefore: "You can view concrete examples of interventions on our ",
      pLink1: "our work",
      pMid: " page or read customer feedback on our ",
      pLink2: "reviews",
      pEnd: " page.",
    },
    faq: {
      title: "Frequently asked questions about house clearance",
      items: [
        {
          q: "How much does house clearance cost in Brussels?",
          a: "The price depends on the volume to be removed, the number of levels, access (parking, stairs, lift) and the presence of buyback-eligible items, which are deducted from the quote. There is no fixed rate for an entire house: a two-façade terraced house differs from a three-storey townhouse. We draw up a free quote after seeing or having the house precisely described, with no hidden costs at billing time.",
        },
        {
          q: "How long does clearing an entire house take?",
          a: "The duration mainly varies according to the volume and number of levels to handle: a typical terraced house usually empties faster than a townhouse with an attic, cellar and outbuildings. Access also plays a role — narrow staircases, no nearby parking or a one-way street can extend the intervention. We estimate the time needed from the first contact, based on the volume described or observed on site, in order to organise the team and the truck accordingly.",
        },
        {
          q: "Do I need to be present for the entire duration of the clearance?",
          a: "Your presence is mainly useful at the start, to jointly confirm what should be removed, kept or assessed for a potential buyback, and at the end for the handover. In between, continuous presence isn't essential if the instructions are clear. As part of an estate settlement or a sale organised by a notary or agency, we can also coordinate access directly with the person handling the file.",
        },
        {
          q: "How are furniture and valuable items found in the house handled?",
          a: "Before any removal, we examine the items present: antique furniture, tableware, jewellery, watches, silverware or paintings may be eligible for buyback, deducted from the clearance amount. This appraisal depends on the condition and actual nature of the items — we don't buy back everything automatically. Furniture in good condition but without buyback value is, when possible, directed to donation rather than removed straight to waste.",
        },
        {
          q: "What happens to bulky items and waste that cannot be bought back or donated?",
          a: "What is neither suitable for buyback nor reusable is sorted by category — wood, metal, appliances, rubble, textiles — before being removed to the appropriate sorting centres. This sorting is done as much as possible directly on site, room by room, rather than mixing everything into the same skip. This removal step is an integral part of the clearance and is included in the initial quote.",
        },
        {
          q: "The house is hard to access (narrow street, no parking, several floors): is that a problem?",
          a: "No, but it affects how the intervention is organised. We adapt the number of people, the type of vehicle and sometimes the time slots based on the actual access: narrow street, limited parking, stairs or floors without a lift. These details should be flagged when requesting your quote so the price and duration estimate is as accurate as possible from the start.",
        },
        {
          q: "Do I need to sort or empty the rooms before your visit?",
          a: "No, that's not necessary: we take care of the on-site sorting, room by room. It is, however, useful to set aside administrative documents, ID papers or personal items you absolutely want to keep, to avoid any confusion on the day of the clearance. If certain rooms or pieces of furniture need to be excluded from the intervention, simply let us know in advance.",
        },
      ],
    },
    ctaFinal: {
      title: "A house to clear in Brussels?",
      p: "Describe your situation to us by phone or via the form below: we'll get back to you with a free quote, tailored to the actual volume of your house.",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(900px 480px at 85% -10%, rgba(27,75,51,0.06), transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-[1200px] px-6 py-16 lg:px-10 lg:py-24">
          <nav className="text-xs text-ink/50">
            <Link href="/" className="hover:text-accent">{c.breadcrumbHome}</Link>
            {" / "}
            <span className="text-ink/70">{c.breadcrumbCurrent}</span>
          </nav>

          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-4 max-w-2xl text-lg font-semibold text-accent">
            {c.highlight}
          </p>

          <p className="mt-5 max-w-2xl text-ink/70 leading-relaxed">
            {c.heroText}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* DEROULEMENT */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.process.title}
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
            {c.process.p}
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.process.steps.map((step) => (
              <div key={step.n} className="rounded-2xl border border-sand-line bg-sand p-5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-900 text-sm font-bold text-white">
                  {step.n}
                </div>
                <p className="mt-3 font-bold text-ink">{step.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-neutral-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.situations.title}
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
            {c.situations.pBefore}
            <Link href="/blog/vide-maison-succession-bruxelles" className="font-semibold text-accent hover:text-ink">
              {c.situations.pLink}
            </Link>
            {c.situations.pAfter}
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.situations.items.map((item) => (
              <li key={item} className="flex items-start gap-2 rounded-2xl border border-sand-line bg-white p-4 text-sm font-semibold text-ink">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PRIX */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.prix.title}
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
            {c.prix.p}
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.prix.items.map((item) => (
              <li key={item} className="flex items-start gap-2 rounded-2xl border border-sand-line bg-sand p-4 text-sm font-semibold text-ink">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-3xl leading-relaxed text-neutral-700">
            {c.prix.pAfterBefore}
            <Link href="/tarifs" className="font-semibold text-accent hover:text-ink">
              {c.prix.pAfterLink1}
            </Link>
            {c.prix.pAfterMid}
            <Link href="/blog/combien-coute-vide-maison-bruxelles" className="font-semibold text-accent hover:text-ink">
              {c.prix.pAfterLink2}
            </Link>
            {c.prix.pAfterEnd}
          </p>
        </div>
      </section>

      {/* TRI / RACHAT */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <div className="rounded-[2rem] border border-sage/25 bg-white p-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-sage">
              <GiftIcon className="h-4 w-4" />
              {c.tri.badge}
            </div>
            <h2 className="mt-4 font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.tri.title}
            </h2>
            <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
              {c.tri.p1before}
              <Link href="/rachat-objets" className="font-semibold text-sage hover:text-ink">
                {c.tri.p1link}
              </Link>
              {c.tri.p1end}
            </p>
            <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
              {c.tri.p2before}
              <Link href="/blog/que-deviennent-meubles-apres-debarras" className="font-semibold text-sage hover:text-ink">
                {c.tri.p2link}
              </Link>
              {c.tri.p2end}
            </p>
          </div>
        </div>
      </section>

      {/* ACCES / PREPARATION */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.acces.title}
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
            {c.acces.p1}
          </p>
          <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
            {c.acces.p2before}
            <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent hover:text-ink">
              {c.acces.p2link}
            </Link>
            {c.acces.p2end}
          </p>
        </div>
      </section>

      {/* DIFFERENCIATION */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.diff.title}
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-neutral-700">
            {c.diff.pBefore}
            <Link href="/debarras-appartement" className="font-semibold text-accent hover:text-ink">
              {c.diff.link1}
            </Link>
            {c.diff.pMid1}
            <Link href="/debarras-cave-grenier-garage" className="font-semibold text-accent hover:text-ink">
              {c.diff.link2}
            </Link>
            {c.diff.pMid2}
            <Link href="/vide-maison" className="font-semibold text-accent hover:text-ink">
              {c.diff.link3}
            </Link>
            {c.diff.pEnd}
          </p>
        </div>
      </section>

      {/* POURQUOI NOUS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.pourquoi.title}
          </h2>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.pourquoi.items.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-sand p-5">
                <ShieldCheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm font-semibold text-ink">{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-3xl leading-relaxed text-neutral-700">
            {c.pourquoi.pBefore}
            <Link href="/realisations" className="font-semibold text-accent hover:text-ink">
              {c.pourquoi.pLink1}
            </Link>
            {c.pourquoi.pMid}
            <Link href="/avis" className="font-semibold text-accent hover:text-ink">
              {c.pourquoi.pLink2}
            </Link>
            {c.pourquoi.pEnd}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.faq.title}
          </h2>
          <div className="mt-6 space-y-4">
            {c.faq.items.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-white p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <div className="rounded-[2rem] border border-sand-line bg-sand p-8 text-center md:p-12">
            <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.ctaFinal.title}
            </h2>
            <p className="mx-auto mt-3 max-w-xl leading-relaxed text-neutral-700">
              {c.ctaFinal.p}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
                {PHONE_DISPLAY}
              </CtaLink>
              <CtaLink href="#contactus" variant="sand" icon={ArrowRightIcon}>
                {c.freeQuote}
              </CtaLink>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
